import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Search, MapPin, Copy, Check, FileText, Edit, UserPlus, Trash2 } from 'lucide-react';

import AssignNurseModal, { StaffMember, Patient } from '../components/AssignNurseModal';
import ZoomableImage from '../components/ZoomableImage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const PLACEHOLDER_IMG = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
const STATUS_OPTIONS = ['Active', 'Suspended', 'Stopped', 'Discharged'];
const DUTY_TYPE_OPTIONS = ['08 Hrs', '12 Dual', '12 Day', '12 Night', '24 Hrs'];

const getStatusStyle = (status: string) => {
  const map: Record<string, { bg: string; color: string; border: string; dot: string }> = {
    Active:     { bg: '#f0fdf4', color: '#15803d', border: '#86efac', dot: '#22c55e' },
    Stopped:    { bg: '#fff7ed', color: '#c2410c', border: '#fdba74', dot: '#f97316' },
    Suspended:  { bg: '#fefce8', color: '#854d0e', border: '#fde047', dot: '#eab308' },
    Discharged: { bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5', dot: '#ef4444' },
  };
  return map[status] || { bg: '#f8fafc', color: '#475569', border: '#cbd5e1', dot: '#94a3b8' };
};

interface StaffInfo {
  id?: number;
  full_name?: string;
  staff_type?: string;
}

interface Assignment {
  id: number;
  start_date: string;
  end_date: string;
  shift_date?: string;
  // API returns separate nurse/physio objects
  nurse?: StaffInfo;
  physio?: StaffInfo;
  nurse_id?: number;
  physio_id?: number;
  second_nurse?: StaffInfo;
  second_physio?: StaffInfo;
  second_nurse_id?: number;
  second_physio_id?: number;
  // Legacy unified fields (kept for backwards compat)
  staff?: StaffInfo;
  staff_id?: number;
  staff_type?: string;
  second_staff?: StaffInfo;
  second_staff_id?: number;
  second_staff_type?: string;
  shift_type: string;
  rate: number;
  second_shift_type?: string;
  second_rate?: number;
  second_total_charge?: number;
  allStaff?: any[];
  patient: { id: number; patient_name: string };
  patient_id?: number;
  status?: string;
  total_charge: number;
  in_time: string;
  out_time: string;
}

type PatientView = 'all' | 'unassigned' | 'assigned';
type AssignmentView = 'daily' | 'all' | 'completed';

const CopyButton = ({ text }: { text: string | undefined }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (text) { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };
  return (
    <button onClick={handleCopy} title={copied ? "Copied!" : "Copy"} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'inline-flex', marginLeft: '6px', padding: '2px' }}>
      {copied ? <Check size={13} color="#16a34a" /> : <Copy size={13} color="#475569" />}
    </button>
  );
};

const CopyableText = ({ text, children }: { text: string | undefined, children: React.ReactNode }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (text) { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };
  return (
    <div onClick={handleCopy} title="Click to copy" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', width: 'fit-content' }}>
      {children}
      <div style={{ marginLeft: '6px', display: 'flex', alignItems: 'center' }}>
        {copied ? <Check size={13} color="#16a34a" /> : <Copy size={13} color="#475569" />}
      </div>
    </div>
  );
};

const STAFF_TYPE_COLORS: Record<string, { bg: string; dot: string; text: string; border: string }> = {
  nurse:      { bg: '#f0f7ff', dot: '#2563eb', text: '#1e40af', border: '#dbeafe' },
  physio:     { bg: '#faf5ff', dot: '#7c3aed', text: '#6d28d9', border: '#e9d5ff' },
  management: { bg: '#fff7ed', dot: '#c2410c', text: '#9a3412', border: '#fed7aa' },
};

const getStaffColors = (type?: string) => STAFF_TYPE_COLORS[type || 'nurse'] || STAFF_TYPE_COLORS.nurse;
const getStaffName = (staff: StaffInfo | undefined | null) =>
  !staff ? 'Unknown' : (staff.full_name || `Staff #${staff.id}`);

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return '-';
  const parts = dateString.split('-');
  if (parts.length === 3) {
    if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return dateString;
  }
  return dateString;
};

const getClientNames = (patient: any) => {
  const clients = patient.patient_clients || patient.clients_info || [];
  if (Array.isArray(clients) && clients.length > 0)
    return [...clients].sort((a: any, b: any) => (a.id || 0) - (b.id || 0));
  if (patient.client_name) return [{ client_name: patient.client_name }];
  return [];
};

// What to call a case in a confirm / toast: the first client, else the patient.
const getCaseLabel = (patient: any) => {
  const c = getClientNames(patient)[0];
  return String(c?.client_name || c?.name || patient?.patient_name || 'This case').trim();
};

const getDutyDisplay = (p: Patient) => {
  if ((p.duty_type === 'Specify' || p.duty_type === 'Other') && p.duty_type_specify) return p.duty_type_specify;
  return p.duty_type || '-';
};

const getImageUrl = (path: string | undefined) =>
  path ? (path.startsWith('http') ? path : `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`) : PLACEHOLDER_IMG;


const AssignmentDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [activeMainTab, setActiveMainTab] = useState<'patients' | 'assignments'>('patients');
  const [activePatientView, setActivePatientView] = useState<PatientView>('all');
  const [activeAssignmentView, setActiveAssignmentView] = useState<AssignmentView>('daily');

  const [hoverRowId, setHoverRowId] = useState<number | null>(null);
  const [isAddingCase, setIsAddingCase] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [allPatientsList, setAllPatientsList] = useState<Patient[]>([]);

  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });

  const [nurses, setNurses] = useState<StaffMember[]>([]);
  const [physios, setPhysios] = useState<StaffMember[]>([]);
  const [masterPatients, setMasterPatients] = useState<Patient[]>([]);
  const [masterAssignments, setMasterAssignments] = useState<Assignment[]>([]);
  const [dateAssignments, setDateAssignments] = useState<Assignment[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [openStatusId, setOpenStatusId] = useState<number | null>(null);
  const [openDutyTypeId, setOpenDutyTypeId] = useState<number | null>(null);

  useEffect(() => {
    if (openStatusId === null) return;
    const close = () => setOpenStatusId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openStatusId]);

  useEffect(() => {
    if (openDutyTypeId === null) return;
    const close = () => setOpenDutyTypeId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openDutyTypeId]);

  const handleStatusChange = async (patient: Patient, newStatus: string) => {
    const token = localStorage.getItem('accessToken');
    try {
      const { data: current } = await axios.get(`${API_BASE_URL}/api/patients/${patient.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const formData = new FormData();
      formData.append('patient_data', JSON.stringify({
        ...current, status: newStatus,
        clients_info: (current.patient_clients || []).map((c: any) => ({
          id: c.id, name: c.client_name, relationship: c.relationship,
          contact_number: c.contact_number_1, secondary_contact_number: c.contact_number_2, email: c.email,
        })),
      }));
      await axios.patch(`${API_BASE_URL}/api/patients/${patient.id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setMasterPatients(prev => prev.map(p => p.id === patient.id ? { ...p, status: newStatus } : p));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDutyTypeChange = async (patient: Patient, newDutyType: string) => {
    const token = localStorage.getItem('accessToken');
    try {
      const { data: current } = await axios.get(`${API_BASE_URL}/api/patients/${patient.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const formData = new FormData();
      formData.append('patient_data', JSON.stringify({
        ...current, duty_type: newDutyType,
        clients_info: (current.patient_clients || []).map((c: any) => ({
          id: c.id, name: c.client_name, relationship: c.relationship,
          contact_number: c.contact_number_1, secondary_contact_number: c.contact_number_2, email: c.email,
        })),
      }));
      await axios.patch(`${API_BASE_URL}/api/patients/${patient.id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setMasterPatients(prev => prev.map(p => p.id === patient.id ? { ...p, duty_type: newDutyType } : p));
    } catch (err) {
      toast.error('Failed to update duty type');
    }
  };

  const handleDateChange = (days: number) => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + days);
    setSelectedDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`);
  };

  // Taking a case off this dashboard is the ONLY thing that hides it here — the
  // case status is deliberately left alone, so setting a case to Suspended /
  // Stopped / Discharged keeps the row (with its new badge) on the board.
  const handleRemovePatient = async (patient: Patient) => {
    const who = getCaseLabel(patient);
    if (!window.confirm(`Remove ${who} from the Operations Dashboard?\n\nThe case status is left as it is and the case stays in the client list — you can put it back anytime from "New Case".`)) return;
    const token = localStorage.getItem('accessToken');
    const snapshot = masterPatients;
    setMasterPatients(prev => prev.filter(p => p.id !== patient.id));
    try {
      // Membership only. The patient endpoint applies the JSON with exclude_unset,
      // so this single field is a safe partial update — nothing else is touched.
      const formData = new FormData();
      formData.append('patient_data', JSON.stringify({ on_assignment_board: false }));
      await axios.patch(`${API_BASE_URL}/api/patients/${patient.id}`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`${who} removed from the dashboard`);
    } catch (err) {
      console.error("Failed to remove case:", err);
      setMasterPatients(snapshot);
      toast.error('Could not remove the case. Please try again.');
    }
  };

  const searchExistingPatients = async (query: string) => {
    setGlobalSearch(query);
    if (query.length < 2) { setAllPatientsList([]); return; }
    const token = localStorage.getItem('accessToken');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/patients/`, { headers: { 'Authorization': `Bearer ${token}` }, params: { limit: 300 } });
      const q = query.toLowerCase();
      // Only cases that are OFF the dashboard — the exact complement of what the
      // table shows, so nothing can be listed here and on the board at once.
      setAllPatientsList(res.data.filter((p: any) => {
        const pMatch = (p.patient_name || '').toLowerCase().includes(q);
        const cMatch = (p.patient_clients || []).some((c: any) => (c.client_name || '').toLowerCase().includes(q));
        return (pMatch || cMatch) && p.on_assignment_board === false;
      }));
    } catch (e) { console.error("Search failed", e); }
  };

  // Puts a case back on the dashboard. Its status is NOT touched — a case added
  // back while Stopped shows up with a Stopped badge, and whoever is looking at
  // it decides whether that should change.
  const handleAddExistingPatient = async (patient: any) => {
    const token = localStorage.getItem('accessToken');
    const who = getCaseLabel(patient);
    try {
      const formData = new FormData();
      formData.append('patient_data', JSON.stringify({ on_assignment_board: true }));
      await axios.patch(`${API_BASE_URL}/api/patients/${patient.id}`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIsAddingCase(false);
      setGlobalSearch('');
      setAllPatientsList([]);
      await fetchPatients();
      toast.success(`${who} added to the dashboard`);
    } catch (e) {
      console.error("Error adding case", e);
      toast.error('Could not add the case. Please try again.');
    }
  };

  const getFormattedDate = () => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  // --- FETCH STAFF ---
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    const h = { 'Authorization': `Bearer ${token}` };

    axios.get(`${API_BASE_URL}/api/nurses/`, { headers: h })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data as any).results || [];
        setNurses(data.map((n: any) => ({ ...n, staff_type: 'nurse' })));
      })
      .catch(e => console.error("Failed to load nurses", e));

    axios.get(`${API_BASE_URL}/api/physiotherapists/`, { headers: h })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data as any).results || [];
        setPhysios(data.map((p: any) => ({ ...p, staff_type: 'physio' })));
      })
      .catch(e => console.error("Failed to load physios", e));
  }, []);

  // --- FETCH PATIENTS & ASSIGNMENTS ---
  const fetchPatients = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    const pRes = await axios.get<Patient[]>(`${API_BASE_URL}/api/patients/`, { headers: { 'Authorization': `Bearer ${token}` } });
    setMasterPatients(pRes.data);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); setError(null);
      const token = localStorage.getItem('accessToken');
      if (!token) { setError("Authentication required."); setLoading(false); return; }
      try {
        if (activeMainTab === 'patients') {
          const pRes = await axios.get<Patient[]>(`${API_BASE_URL}/api/patients/`, { headers: { 'Authorization': `Bearer ${token}` } });
          setMasterPatients(pRes.data);
          try {
            const aRes = await axios.get<Assignment[]>(`${API_BASE_URL}/api/assignments`, {
              headers: { 'Authorization': `Bearer ${token}` },
              params: { start_date: selectedDate, end_date: selectedDate }
            });
            setDateAssignments(aRes.data);
          } catch (aErr) {
            console.error("Assignments fetch failed (check DB migration):", aErr);
            setDateAssignments([]);
          }
        } else {
          let params: any = {};
          if (activeAssignmentView === 'daily') params = { start_date: selectedDate, end_date: selectedDate };
          const res = await axios.get<Assignment[]>(`${API_BASE_URL}/api/assignments`, {
            headers: { 'Authorization': `Bearer ${token}` }, params
          });
          if (activeAssignmentView === 'daily') setDateAssignments(res.data);
          else setMasterAssignments(res.data);
        }
      } catch (err) { console.error(err); setError("Failed to fetch data."); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [activeMainTab, selectedDate, activeAssignmentView]);

  const assignedPatientIds = useMemo(() =>
    new Set(dateAssignments.map(a => a.patient?.id || a.patient_id)), [dateAssignments]);

  const displayedPatients = useMemo(() => {
    if (activeMainTab !== 'patients') return [];
    // Membership, not status: a case is listed until someone takes it off with
    // "Remove Case" (on_assignment_board = false). Changing a case's status only
    // changes its badge — Suspended / Stopped / Discharged cases stay on the
    // board so the team can still see and work them.
    let filtered = masterPatients.filter(p => (p as any).on_assignment_board !== false);
    if (activePatientView === 'assigned') filtered = filtered.filter(p => assignedPatientIds.has(p.id));
    else if (activePatientView === 'unassigned') filtered = filtered.filter(p => !assignedPatientIds.has(p.id));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => {
        const pName = (p.patient_name || '').toLowerCase();
        const cMatch = getClientNames(p).some((c: any) => (c.client_name || c.name || '').toLowerCase().includes(q));
        return pName.includes(q) || cMatch || String(p.id).includes(q);
      });
    }
    const STATUS_SORT_ORDER = ['Active', 'Suspended', 'Stopped', 'Discharged'];
    const DUTY_SORT_ORDER = ['08 Hrs', '12 Dual', '12 Day', '12 Night', '24 Hrs', 'Nursing Visit'];
    return filtered.sort((a, b) => {
      const sA = STATUS_SORT_ORDER.indexOf(a.status || ''); const sAi = sA === -1 ? 99 : sA;
      const sB = STATUS_SORT_ORDER.indexOf(b.status || ''); const sBi = sB === -1 ? 99 : sB;
      if (sAi !== sBi) return sAi - sBi;
      const typeA = a.duty_type || a.duty_type_specify || '';
      const typeB = b.duty_type || b.duty_type_specify || '';
      let iA = DUTY_SORT_ORDER.indexOf(typeA); if (iA === -1) iA = 999;
      let iB = DUTY_SORT_ORDER.indexOf(typeB); if (iB === -1) iB = 999;
      if (iA !== iB) return iA - iB;
      const cA = (getClientNames(a)[0]?.client_name || '').trim().toLowerCase();
      const cB = (getClientNames(b)[0]?.client_name || '').trim().toLowerCase();
      return cA.localeCompare(cB);
    });
  }, [activeMainTab, activePatientView, masterPatients, assignedPatientIds, searchQuery]);

  const displayedAssignments = useMemo(() => {
    if (activeMainTab !== 'assignments') return [];
    const rawData = activeAssignmentView === 'daily' ? dateAssignments : masterAssignments;
    return rawData.reduce((acc: any[], curr) => {
      // Normalize nurse/physio into a unified primaryStaff / secondaryStaff
      const primaryStaff = curr.nurse  ? { ...curr.nurse,  staff_type: 'nurse'  }
                         : curr.physio ? { ...curr.physio, staff_type: 'physio' }
                         : null;
      const secondaryStaff = curr.second_nurse  ? { ...curr.second_nurse,  staff_type: 'nurse'  }
                           : curr.second_physio ? { ...curr.second_physio, staff_type: 'physio' }
                           : null;

      const pId = curr.patient?.id || curr.patient_id;
      const dateKey = (curr.start_date || '').split('T')[0];
      const key = `${dateKey}-${pId}-${curr.shift_type}`;
      const existingIndex = acc.findIndex(item => item.groupKey === key);

      if (existingIndex > -1) {
        [primaryStaff, secondaryStaff].filter(Boolean).forEach(s => {
          const exists = acc[existingIndex].allStaff.some((e: any) => e.id === s!.id && e.staff_type === s!.staff_type);
          if (!exists) acc[existingIndex].allStaff.push(s);
        });
      } else {
        acc.push({
          ...curr, groupKey: key, primaryStaff, secondaryStaff,
          allStaff: [primaryStaff, secondaryStaff].filter(Boolean),
          displayDate: dateKey,
        });
      }
      return acc;
    }, []);
  }, [activeMainTab, activeAssignmentView, masterAssignments, dateAssignments]);

  const handleAssignClick = (patient: Patient) => { setSelectedPatient(patient); setIsAssignModalOpen(true); };
  const handleCaseSheetClick = (patientId: number) => window.open(`/casesheet/${patientId}`, '_blank', 'noopener,noreferrer');
  const handleAssignmentSuccess = () => { setIsAssignModalOpen(false); setSelectedPatient(null); window.location.reload(); };

  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'nowrap' as 'nowrap' },
    title: { margin: 0, fontSize: '1.875rem', fontWeight: 700, color: '#0f172a' },
    tabsContainer: { display: 'flex', gap: '2rem', borderBottom: '2px solid #e2e8f0', marginBottom: '2rem' },
    mainTab: { padding: '1rem 0.5rem', cursor: 'pointer', borderBottom: '2px solid transparent', color: '#64748b', fontWeight: 600, fontSize: '1rem', marginBottom: '-2px' },
    mainTabActive: { color: '#2563eb', borderBottomColor: '#2563eb' },
    dateNavContainer: { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'white', padding: '8px 16px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' },
    dateNavBtn: { display: 'flex', alignItems: 'center', gap: '5px', border: '1px solid #e2e8f0', backgroundColor: 'white', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', color: '#64748b', fontSize: '0.9rem', fontWeight: 600 },
    dateDisplay: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' },
    subTab: { padding: '0.5rem 1.25rem', cursor: 'pointer', borderRadius: '9999px', backgroundColor: 'white', color: '#64748b', fontSize: '0.875rem', border: '1px solid #cbd5e1', fontWeight: 500 },
    subTabActive: { backgroundColor: '#2563eb', color: 'white', borderColor: '#2563eb' },
    searchContainer: { position: 'relative', width: '300px' },
    searchInput: { width: '100%', padding: '0.6rem 1rem 0.6rem 2.4rem', borderRadius: '20px', border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none', backgroundColor: '#fff' },
    tableCard: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', overflow: 'hidden' },
    tableWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left', color: '#1d2631ff' },
    thead: { backgroundColor: '#86afd8ff', borderBottom: '1px solid #e2e8f0' },
    th: { padding: '1rem', color: '#475569', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', border: '1px solid #c5cbd3ff', textAlign: 'center' },
    tbody: { backgroundColor: 'white' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.1s' },
    td: { padding: '0.75rem 1rem', verticalAlign: 'middle', border: '1px solid #c5cbd3ff' },
    avatar: { width: '100px', height: '100px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e2e8f0' },
    badge: { display: 'inline-flex', alignItems: 'center', padding: '0.25rem 0.6rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' },
    badgeAssigned: { backgroundColor: '#dcfce7', color: '#166534' },
    badgeUnassigned: { backgroundColor: '#fee2e2', color: '#991b1b' },
    btnPrimary: { background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', width: '100%' },
    btnCaseSheet: { backgroundColor: 'white', color: '#16a34a', border: '1px solid #86efac', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%', marginBottom: '6px' },
    btnEdit: { background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)', color: 'white', border: 'none', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%' },
  };

  return (
    <div style={styles.page}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      <header style={styles.header}>
        <h1 style={{ ...styles.title, flexShrink: 0 }}>Operations Dashboard</h1>

        {/* DATE NAVIGATOR — center */}
        <div style={{ ...styles.dateNavContainer, flexShrink: 0 }}>
          <button style={styles.dateNavBtn} onClick={() => handleDateChange(-1)}><ChevronLeft size={16} /> Previous Day</button>
          <div style={styles.dateDisplay}>
            <CalendarIcon size={20} color="#3b82f6" />
            <span>{getFormattedDate()}</span>
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
              style={{ marginLeft: '10px', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '4px', fontSize: '0.9rem', color: '#64748b' }} />
          </div>
          <button style={styles.dateNavBtn} onClick={() => handleDateChange(1)}>Next Day <ChevronRight size={16} /></button>
        </div>

        {/* PATIENT TOOLBAR — right */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem', flex: 1, minWidth: 0 }}>
          <button style={{ ...styles.subTab, flexShrink: 0, ...(activePatientView === 'all' ? styles.subTabActive : {}) }} onClick={() => { setActivePatientView('all'); setActiveMainTab('patients'); }}>All Cases</button>
          <button style={{ ...styles.subTab, flexShrink: 0, ...(activePatientView === 'unassigned' ? styles.subTabActive : {}) }} onClick={() => { setActivePatientView('unassigned'); setActiveMainTab('patients'); }}>Unassigned</button>
          <div style={{ position: 'relative', flexShrink: 1, minWidth: '140px', width: '220px' }}>
            <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input type="text" placeholder="Filter..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} style={{ ...styles.searchInput, width: '100%', paddingLeft: '32px', boxSizing: 'border-box' as 'border-box' }} />
          </div>

          {/* Add New Case — fixed-width button, search opens as dropdown */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setIsAddingCase(!isAddingCase)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: isAddingCase ? '#1d4ed8' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white', border: 'none', borderRadius: '8px',
                padding: '0.45rem 1rem', fontSize: '0.8rem', fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
              }}
            >
              <UserPlus size={15} /> New Case
            </button>

            {isAddingCase && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                width: '320px', backgroundColor: 'white', borderRadius: '14px',
                boxShadow: '0 16px 40px rgba(0,0,0,0.13)', border: '1px solid #e2e8f0',
                padding: '14px', zIndex: 1000,
              }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                  Add a removed case back
                </div>
                <div style={{ position: 'relative' }}>
                  <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    autoFocus type="text" placeholder="Search by patient or client name..."
                    value={globalSearch} onChange={(e) => searchExistingPatients(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem 0.75rem 0.5rem 2.2rem', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' as 'border-box', backgroundColor: '#f8fafc', color: '#0f172a' }}
                  />
                </div>
                {globalSearch.trim().length >= 2 && allPatientsList.length === 0 && (
                  <div style={{ marginTop: '8px', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '0.72rem', color: '#64748b', lineHeight: 1.45 }}>
                    No removed case matches that. Only cases taken off with{' '}
                    <strong style={{ color: '#475569' }}>Remove Case</strong> are listed here — a case is still on the dashboard whatever its status.
                  </div>
                )}
                {allPatientsList.length > 0 && (
                  <div style={{ marginTop: '8px', maxHeight: '240px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    {allPatientsList.map(p => {
                      const clients = getClientNames(p);
                      const mainClient = clients[0]?.client_name || clients[0]?.name || 'N/A';
                      return (
                        <div key={p.id} onClick={() => handleAddExistingPatient(p)}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '2px' }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}>
                          <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>{mainClient}</span>
                          <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.patient_name}{p.status ? ` · ${p.status}` : ''}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button
                  onClick={() => { setIsAddingCase(false); setGlobalSearch(''); setAllPatientsList([]); }}
                  style={{ marginTop: '10px', width: '100%', padding: '0.4rem', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* MAIN TABS */}
      <div style={styles.tabsContainer}>
        <div style={{ ...styles.mainTab, ...(activeMainTab === 'patients' ? styles.mainTabActive : {}) }} onClick={() => setActiveMainTab('patients')}>
          Case Assignment Status
        </div>
        <div style={{ ...styles.mainTab, ...(activeMainTab === 'assignments' ? styles.mainTabActive : {}) }} onClick={() => setActiveMainTab('assignments')}>
          Staff Schedules & Assignments
        </div>
      </div>

      {/* ================================================================ */}
      {/* PATIENTS / ACTIVE CASES TAB                                       */}
      {/* ================================================================ */}
      {activeMainTab === 'patients' && (
        <>

          <div style={styles.tableCard}>
            <div style={styles.tableWrapper}>
              <table style={{ ...styles.table, tableLayout: 'fixed' }}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={{ ...styles.th, width: '46px' }}>S.No</th>
                    <th style={{ ...styles.th, width: '88px', whiteSpace: 'normal' }}>Duty<br />Type</th>
                    <th style={{ ...styles.th, width: '110px' }}>Photo</th>
                    <th style={{ ...styles.th, width: '72px' }}>Start<br />Date</th>
                    <th style={{ ...styles.th, width: '110px' }}>Client Name</th>
                    <th style={{ ...styles.th, width: '130px' }}>Mobile</th>
                    <th style={{ ...styles.th, width: '120px' }}>Patient</th>
                    <th style={{ ...styles.th, width: '52px' }}>Age/<br />Sex</th>
                    <th style={{ ...styles.th, width: '150px' }}>Address</th>
                    <th style={{ ...styles.th, width: '130px' }}>Doctor<br />Name</th>
                    <th style={{ ...styles.th, width: '130px' }}>Hospital<br />Name</th>
                    <th style={{ ...styles.th, width: '72px' }}>Manager</th>
                    <th style={{ ...styles.th, width: '90px' }}>Status</th>
                    <th style={{ ...styles.th, width: '140px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody style={styles.tbody}>
                  {loading && <tr><td colSpan={14} style={{ ...styles.td, textAlign: 'center', padding: '3rem' }}>Loading...</td></tr>}
                  {!loading && displayedPatients.length === 0 && <tr><td colSpan={14} style={{ ...styles.td, textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>No patients found.</td></tr>}
                  {!loading && displayedPatients.map((p, i) => {
                    const isAssigned = assignedPatientIds.has(p.id);
                    const clients = getClientNames(p);
                    const td0 = p.treating_details?.[0];
                    return (
                      <tr key={p.id} style={{ ...styles.tr, backgroundColor: hoverRowId === p.id ? '#f8fafc' : 'white' }}
                        onMouseEnter={() => setHoverRowId(p.id)} onMouseLeave={() => setHoverRowId(null)}>

                        {/* S.No */}
                        <td style={{ ...styles.td, textAlign: 'center', backgroundColor: '#f8fafc', fontWeight: 600, color: '#94a3b8', fontSize: '0.76rem' }}>{i + 1}</td>

                        {/* Duty Type */}
                        <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'normal' }}>
                          {(() => {
                            const isOpen = openDutyTypeId === p.id;
                            return (
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenDutyTypeId(isOpen ? null : p.id); }}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    padding: '3px 8px 3px 7px', borderRadius: '999px',
                                    fontSize: '0.68rem', fontWeight: 700,
                                    border: '1.5px solid #cbd5e1',
                                    backgroundColor: '#f8fafc', color: '#1e293b',
                                    cursor: 'pointer', outline: 'none',
                                    letterSpacing: '0.02em', whiteSpace: 'nowrap',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                                  }}
                                >
                                  {getDutyDisplay(p)}
                                  <svg viewBox="0 0 10 10" width="8" height="8" fill="none" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1,3 5,7 9,3" />
                                  </svg>
                                </button>
                                {isOpen && (
                                  <div onClick={e => e.stopPropagation()} style={{
                                    position: 'absolute', top: 'calc(100% + 5px)', left: '50%', transform: 'translateX(-50%)',
                                    backgroundColor: 'white', borderRadius: '10px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
                                    padding: '4px', zIndex: 999, minWidth: '90px',
                                  }}>
                                    {DUTY_TYPE_OPTIONS.map(opt => {
                                      const isSelected = p.duty_type === opt;
                                      return (
                                        <div
                                          key={opt}
                                          onClick={() => { handleDutyTypeChange(p, opt); setOpenDutyTypeId(null); }}
                                          style={{
                                            display: 'flex', alignItems: 'center', gap: '7px',
                                            padding: '6px 10px', borderRadius: '7px',
                                            fontSize: '0.72rem', fontWeight: isSelected ? 700 : 500,
                                            color: isSelected ? '#1e293b' : '#334155',
                                            backgroundColor: isSelected ? '#f1f5f9' : 'transparent',
                                            cursor: 'pointer', whiteSpace: 'nowrap',
                                          }}
                                          onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = '#f8fafc'; }}
                                          onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                                        >
                                          {opt}
                                          {isSelected && (
                                            <svg style={{ marginLeft: 'auto' }} viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <polyline points="2,6 5,9 10,3" />
                                            </svg>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>

                        {/* Photo */}
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <ZoomableImage src={getImageUrl(p.photo_url)} alt="Photo" style={styles.avatar as React.CSSProperties} fallbackSrc={PLACEHOLDER_IMG} />
                        </td>

                        {/* Start Date */}
                        <td style={{ ...styles.td, textAlign: 'center', fontSize: '0.76rem', whiteSpace: 'nowrap' }}>{formatDate(p.case_start_date)}</td>

                        {/* Client Name */}
                        <td style={{ ...styles.td, textAlign: 'left', wordBreak: 'break-word' }}>
                          {clients.length === 0 ? '—' : clients.map((c: any, ci: number) => (
                            <div key={ci} style={{ marginBottom: ci < clients.length - 1 ? '2px' : 0, fontWeight: 700, color: '#0f172a', fontSize: '0.76rem' }}>
                              <CopyableText text={c.client_name || c.name}>
                                <span>{c.client_name || c.name}</span>
                              </CopyableText>
                            </div>
                          ))}
                        </td>

                        {/* Mobile */}
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {clients.length === 0 ? '—' : clients.map((c: any, ci: number) => (
                            <div key={ci} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: ci < clients.length - 1 ? '4px' : 0 }}>
                              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                <CopyableText text={c.contact_number_1 || c.contact_number}>
                                  {c.contact_number_1 || c.contact_number || '—'}
                                </CopyableText>
                              </div>
                              {(c.contact_number_2 || c.secondary_contact_number) && (
                                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                                  <CopyableText text={c.contact_number_2 || c.secondary_contact_number}>
                                    {c.contact_number_2 || c.secondary_contact_number}
                                  </CopyableText>
                                </div>
                              )}
                            </div>
                          ))}
                        </td>

                        {/* Patient */}
                        <td style={{ ...styles.td, textAlign: 'left', wordBreak: 'break-word' }}>
                          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.76rem' }}>{p.patient_name}</div>
                        </td>

                        {/* Age/Sex */}
                        <td style={{ ...styles.td, textAlign: 'center', fontSize: '0.76rem', whiteSpace: 'nowrap' }}>{p.age || '—'} / {p.sex ? p.sex.charAt(0).toUpperCase() : '—'}</td>

                        {/* Address */}
                        <td style={{ ...styles.td, textAlign: 'left', wordBreak: 'break-word' }}>
                          <div style={{ fontSize: '0.76rem', lineHeight: '1.4', color: '#1e293b' }}>{p.address}</div>
                          {p.location_link && (
                            <a href={p.location_link} target="_blank" rel="noopener noreferrer"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: '#2563eb', textDecoration: 'none', marginTop: '4px' }}>
                              <MapPin size={12} /> Map
                            </a>
                          )}
                        </td>

                        {/* Doctor Name */}
                        <td style={{ ...styles.td, textAlign: 'center', fontSize: '0.76rem' }}>
                          {p.treating_details && p.treating_details.length > 0
                            ? p.treating_details.map((td: any, tdi: number) => (
                              <div key={tdi} style={{ marginBottom: tdi < p.treating_details!.length - 1 ? '2px' : 0 }}>{td.doctor || '—'}</div>
                            ))
                            : '—'}
                        </td>

                        {/* Hospital Name */}
                        <td style={{ ...styles.td, textAlign: 'center', fontSize: '0.76rem' }}>
                          {p.treating_details && p.treating_details.length > 0
                            ? (() => {
                                const hospitals = p.treating_details.map((td: any) => td.hospital).filter(Boolean).filter((h: string, i: number, arr: string[]) => arr.indexOf(h) === i);
                                return hospitals.length > 0
                                  ? hospitals.map((h, hi) => <div key={hi} style={{ marginBottom: hi < hospitals.length - 1 ? '2px' : 0 }}>{h as string}</div>)
                                  : '—';
                              })()
                            : '—'}
                        </td>

                        {/* Manager */}
                        <td style={{ ...styles.td, textAlign: 'center', fontSize: '0.76rem' }}>{p.case_manager || '—'}</td>

                        {/* Case Status */}
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {(() => {
                            const s = getStatusStyle(p.status || '');
                            const isOpen = openStatusId === p.id;
                            return (
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenStatusId(isOpen ? null : p.id); }}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    padding: '3px 8px 3px 7px', borderRadius: '999px',
                                    fontSize: '0.68rem', fontWeight: 700,
                                    border: `1.5px solid ${s.border}`,
                                    backgroundColor: s.bg, color: s.color,
                                    cursor: 'pointer', outline: 'none',
                                    letterSpacing: '0.02em', whiteSpace: 'nowrap',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                                  }}
                                >
                                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: s.dot, flexShrink: 0 }} />
                                  {p.status || '—'}
                                  <svg viewBox="0 0 10 10" width="8" height="8" fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1,3 5,7 9,3" />
                                  </svg>
                                </button>
                                {isOpen && (
                                  <div onClick={e => e.stopPropagation()} style={{
                                    position: 'absolute', top: 'calc(100% + 5px)', left: '50%', transform: 'translateX(-50%)',
                                    backgroundColor: 'white', borderRadius: '10px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
                                    padding: '4px', zIndex: 999, minWidth: '110px',
                                  }}>
                                    {STATUS_OPTIONS.map(opt => {
                                      const os = getStatusStyle(opt);
                                      const isSelected = p.status === opt;
                                      return (
                                        <div
                                          key={opt}
                                          onClick={() => { handleStatusChange(p, opt); setOpenStatusId(null); }}
                                          style={{
                                            display: 'flex', alignItems: 'center', gap: '7px',
                                            padding: '6px 10px', borderRadius: '7px',
                                            fontSize: '0.72rem', fontWeight: isSelected ? 700 : 500,
                                            color: isSelected ? os.color : '#334155',
                                            backgroundColor: isSelected ? os.bg : 'transparent',
                                            cursor: 'pointer', whiteSpace: 'nowrap',
                                          }}
                                          onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = '#f8fafc'; }}
                                          onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                                        >
                                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: os.dot, flexShrink: 0 }} />
                                          {opt}
                                          {isSelected && (
                                            <svg style={{ marginLeft: 'auto' }} viewBox="0 0 12 12" width="10" height="10" fill="none" stroke={os.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <polyline points="2,6 5,9 10,3" />
                                            </svg>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>

                        {/* Actions */}
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 0' }}>
                            {isAssigned ? (
                              <>
                                <button style={{ ...styles.btnCaseSheet, margin: 0 }} onClick={e => { e.stopPropagation(); handleCaseSheetClick(p.id); }}>
                                  <FileText size={14} /> View Sheet
                                </button>
                                <button style={{ ...styles.btnEdit, margin: 0 }} onClick={e => { e.stopPropagation(); handleAssignClick(p); }}>
                                  <Edit size={14} /> Edit Assignment
                                </button>
                              </>
                            ) : (
                              <button style={{ ...styles.btnPrimary, margin: 0 }} onClick={e => { e.stopPropagation(); handleAssignClick(p); }}>
                                <UserPlus size={14} /> Assign Staff
                              </button>
                            )}
                            <button
                              style={{ ...styles.btnCaseSheet, color: '#dc2626', borderColor: '#fecaca', backgroundColor: '#fff1f2', margin: 0 }}
                              onClick={e => { e.stopPropagation(); handleRemovePatient(p); }}>
                              <Trash2 size={14} /> Remove Case
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* ASSIGNMENTS TAB                                                    */}
      {/* ================================================================ */}
      {activeMainTab === 'assignments' && (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <button style={{ ...styles.subTab, ...(activeAssignmentView === 'daily' ? styles.subTabActive : {}) }} onClick={() => setActiveAssignmentView('daily')}>Daily View ({selectedDate})</button>
            <button style={{ ...styles.subTab, ...(activeAssignmentView === 'all' ? styles.subTabActive : {}) }} onClick={() => setActiveAssignmentView('all')}>All History</button>
            <button style={{ ...styles.subTab, ...(activeAssignmentView === 'completed' ? styles.subTabActive : {}) }} onClick={() => setActiveAssignmentView('completed')}>Completed</button>
          </div>

          <div style={styles.tableCard}>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead style={styles.thead}>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Staff Assigned</th>
                    <th style={styles.th}>Client Name</th>
                    <th style={styles.th}>Patient</th>
                    <th style={styles.th}>Shift / Duty</th>
                    <th style={styles.th}>Timings</th>
                  </tr>
                </thead>
                <tbody style={styles.tbody}>
                  {!loading && displayedAssignments.map((a) => {
                    const primaryColors = getStaffColors(a.primaryStaff?.staff_type);
                    const secondaryColors = getStaffColors(a.secondaryStaff?.staff_type);
                    // patient duty type for context
                    const patientRecord = masterPatients.find(p => p.id === (a.patient?.id || a.patient_id));
                    return (
                      <tr key={a.groupKey} style={{ ...styles.tr, backgroundColor: hoverRowId === a.id ? '#f8fafc' : 'white' }}
                        onMouseEnter={() => setHoverRowId(a.id)} onMouseLeave={() => setHoverRowId(null)}>

                        <td style={styles.td}>{formatDate(a.displayDate || a.start_date)}</td>

                        {/* Staff column — nurse or physio */}
                        <td style={styles.td}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {a.primaryStaff && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: primaryColors.bg, padding: '4px 8px', borderRadius: '6px', border: `1px solid ${primaryColors.border}`, width: 'fit-content' }}>
                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: primaryColors.dot }} />
                                <span style={{ fontWeight: 600, color: primaryColors.text, fontSize: '0.8rem' }}>
                                  {getStaffName(a.primaryStaff)}
                                </span>
                                <span style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {a.primaryStaff.staff_type}
                                </span>
                              </div>
                            )}
                            {a.secondaryStaff && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: secondaryColors.bg, padding: '4px 8px', borderRadius: '6px', border: `1px solid ${secondaryColors.border}`, width: 'fit-content' }}>
                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: secondaryColors.dot }} />
                                <span style={{ fontWeight: 600, color: secondaryColors.text, fontSize: '0.8rem' }}>
                                  {getStaffName(a.secondaryStaff)}
                                </span>
                                <span style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {a.secondaryStaff.staff_type}
                                </span>
                              </div>
                            )}
                            {!a.primaryStaff && !a.secondaryStaff && (
                              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Unassigned</span>
                            )}
                          </div>
                        </td>

                        {/* Client Name */}
                        <td style={styles.td}>
                          {getClientNames(patientRecord).map((c: any, ci: number) => (
                            <div key={ci} style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
                              {c.client_name || c.name || '—'}
                            </div>
                          ))}
                          {getClientNames(patientRecord).length === 0 && <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>

                        {/* Patient + Duty Type */}
                        <td style={styles.td}>
                          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>{a.patient?.patient_name || '—'}</div>
                          {patientRecord?.duty_type && (
                            <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '2px' }}>{patientRecord.duty_type}</div>
                          )}
                        </td>

                        <td style={styles.td}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ backgroundColor: '#eff6ff', color: '#2563eb', padding: '4px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '0.75rem', width: 'fit-content' }}>{a.shift_type}</span>
                            {a.second_shift_type && a.second_shift_type !== a.shift_type && (
                              <span style={{ backgroundColor: '#f0fdf4', color: '#166534', padding: '4px 8px', borderRadius: '4px', fontWeight: 700, fontSize: '0.75rem', width: 'fit-content' }}>{a.second_shift_type}</span>
                            )}
                          </div>
                        </td>

                        <td style={styles.td}>
                          <div style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 500 }}>{a.in_time} - {a.out_time}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Pass both staff lists — the modal/drawer decide which to show */}
      <AssignNurseModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={handleAssignmentSuccess}
        patient={selectedPatient}
        nurses={nurses}
        physios={physios}
        selectedDate={selectedDate}
      />
    </div>
  );
};

export default AssignmentDashboard;
